import { useParams } from 'react-router-dom'
import { tenantConfig } from '../../config/tenant'
import Nav from '../../components/Nav'
import Seo from '../../components/Seo'
import Footer from '../../components/Footer'
import calcLandingPages from '../../data/calc-landing-pages.json'

/**
 * CalcLandingPage — thin SEO landing pages for the 6 preserved calculator URLs
 * (D14, locked 2026-07-08: 6 preserve as landing pages + 10 redirect).
 *
 * Each of `/tools/<slug>-calculator` targets the original Google query it used
 * to rank on, reapplies the preserved Yoast <title> + meta description, serves
 * ~400 words of on-topic content, and funnels the visitor to the unified
 * all-in-one calculator via a prominent `/tools#calculator?type=<type>` link.
 *
 * Content is data-driven from `data/calc-landing-pages.json` — one entry per
 * slug — so the SSG build (Phase 10) can emit one static HTML file per calc.
 */

interface CalcLanding {
  slug: string
  title: string
  yoastTitle: string
  metaDescription: string
  bodyParagraphs: string[]
}

const PAGES = calcLandingPages as CalcLanding[]


export default function CalcLandingPage() {
  const { slug } = useParams<{ slug: string }>()
  const page = PAGES.find((p) => p.slug === slug)

  if (!page) {
    return (
      <>
        <Nav />
        <main id="main" className="pt-36 pb-20">
          <section className="max-w-3xl mx-auto px-6">
            <h1 className="font-heading text-3xl text-ink">Calculator not found</h1>
            <p className="text-gs-text-soft mt-2">
              We couldn&apos;t find that calculator page.{' '}
              <a href="/tools" className="underline">Open the all-in-one calculator</a> instead.
            </p>
          </section>
        </main>
        <Footer />
      </>
    )
  }

  // The preserved calc URLs carry a `-calculator` suffix; the unified calculator
  // anchor takes the bare type hint (e.g. stamp-duty-calculator → type=stamp-duty).
  const calcType = page.slug.replace(/-calculator$/, '')
  const calcHref = `/tools#calculator?type=${calcType}`

  return (
    <>
      <Seo
        title={page.yoastTitle || page.title}
        description={page.metaDescription || `${page.title}, a free calculator from ${tenantConfig.name}.`}
        path={`/tools/${page.slug}`}
      />
      <Nav />
      <main id="main" className="pt-36 pb-20">
        {/* HERO */}
        <section className="max-w-3xl mx-auto px-6 pb-10">
          <div className="inline-flex items-center gap-2 border border-[#E8E8E2] rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wider text-gs-text-soft mb-6">
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: tenantConfig.accentColor }} />
            Free calculator
          </div>
          <h1 className="font-heading text-3xl md:text-5xl text-ink font-semibold leading-tight mb-4">
            {page.title}
          </h1>
          <p className="text-lg text-gs-text-soft">{page.metaDescription}</p>
          <div className="mt-8">
            <a
              href={calcHref}
              className="inline-flex items-center px-6 py-3.5 rounded-lg font-semibold text-sm"
              style={{ background: tenantConfig.accentColor, color: tenantConfig.primaryColor }}
            >
              Open the calculator
            </a>
          </div>
        </section>

        {/* BODY — ~400 words of preserved on-topic SEO content */}
        <section className="max-w-3xl mx-auto px-6">
          <div className="space-y-5">
            {page.bodyParagraphs.map((para, i) => (
              <p key={i} className="text-gs-text leading-relaxed">
                {para}
              </p>
            ))}
          </div>
        </section>

        {/* CTA BAND — repeat funnel to the unified calculator */}
        <section className="max-w-3xl mx-auto px-6 mt-14">
          <div
            className="rounded-3xl p-8 md:p-10 grid gap-4 md:grid-cols-[1.5fr_1fr] items-center"
            style={{ background: tenantConfig.primaryColor }}
          >
            <div>
              <h2 className="font-heading text-2xl md:text-3xl mb-2" style={{ color: tenantConfig.accentColor }}>
                Run your own numbers
              </h2>
              <p className="text-white/80">
                Repayments, stamp duty, LMI and borrowing power, all in one place, with a
                {' '}{tenantConfig.name} broker on hand to confirm the real figures.
              </p>
            </div>
            <a
              href={calcHref}
              className="px-6 py-3.5 rounded-lg font-semibold text-sm text-center w-full md:w-auto justify-self-start md:justify-self-end"
              style={{ background: tenantConfig.accentColor, color: tenantConfig.primaryColor }}
            >
              Go to the full calculator
            </a>
          </div>
        </section>

        {/* DISCLAIMER */}
        <section className="max-w-3xl mx-auto px-6 mt-10">
          <p className="text-xs text-gs-text-muted">
            Results are indicative only and not a quote, pre-approval or financial advice.
            Final figures depend on your circumstances and the lender&apos;s assessment.
          </p>
        </section>
      </main>
      <Footer />
    </>
  )
}
